import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/ui/Toast';
import { describeCallsError } from '../utils/apiErrorMessage';
import { callsApi } from '../api/callsApi';
import type { CallAgent, CallAgentInput, CallAgentUpdate } from '../api/callsApi';
import { callsKeys } from '../queryKeys';

export function useCallAgentActions(locationId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: callsKeys.agents(locationId) });
    queryClient.invalidateQueries({ queryKey: callsKeys.numbers(locationId) });
    queryClient.invalidateQueries({ queryKey: callsKeys.health(locationId) });
  };

  const create = useMutation({
    mutationFn: (input: CallAgentInput) => callsApi.createAgent(input),
    onSuccess: (created) => {
      toast.success('Agent created');
      // Put the new agent in the cached list *before* the caller navigates to
      // it — otherwise the detail page mounts with the previous list, finds no
      // such agent and bounces back (losing "Approve & activate").
      queryClient.setQueryData<CallAgent[]>(callsKeys.agents(locationId), (old) => [
        created,
        ...(old ?? []).filter((a) => a.id !== created.id),
      ]);
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to create agent')),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CallAgentUpdate }) =>
      callsApi.updateAgent(id, locationId, data),
    onSuccess: () => {
      toast.success('Agent saved');
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to save agent')),
  });

  // `save` = the builder's current draft. Activation validates and pushes the
  // stored row, so unsaved on-screen edits are saved first — otherwise it
  // could reject a field the user can see is filled in, or go live without
  // the edits (and the success handler would then wipe them from the form).
  const activate = useMutation({
    mutationFn: async ({
      id,
      existingPhoneNumber,
      save,
    }: {
      id: string;
      existingPhoneNumber?: string;
      save?: CallAgentUpdate;
    }) => {
      if (save) await callsApi.updateAgent(id, locationId, save);
      return callsApi.activateAgent(id, locationId, existingPhoneNumber);
    },
    onSuccess: () => {
      toast.success('Agent is now live');
      invalidate();
      queryClient.invalidateQueries({ queryKey: callsKeys.availableNumbers(locationId) });
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to activate agent')),
  });

  const pause = useMutation({
    mutationFn: (id: string) => callsApi.pauseAgent(id, locationId),
    onSuccess: () => {
      toast.success('Agent paused');
      invalidate();
      queryClient.invalidateQueries({ queryKey: callsKeys.availableNumbers(locationId) });
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to pause agent')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => callsApi.deleteAgent(id, locationId),
    onSuccess: () => {
      toast.success('Agent deleted');
      invalidate();
      queryClient.invalidateQueries({ queryKey: callsKeys.availableNumbers(locationId) });
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to delete agent')),
  });

  const deleteNumber = useMutation({
    mutationFn: (phoneNumberId: string) => callsApi.deleteNumber(phoneNumberId, locationId),
    onSuccess: () => {
      toast.success('Number released');
      invalidate();
      queryClient.invalidateQueries({ queryKey: callsKeys.availableNumbers(locationId) });
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to release number')),
  });

  const updateFallback = useMutation({
    mutationFn: ({
      phoneNumberId,
      fallbackNumber,
    }: {
      phoneNumberId: string;
      fallbackNumber: string | null;
    }) => callsApi.updateNumberFallback(phoneNumberId, locationId, fallbackNumber),
    onSuccess: () => {
      toast.success('Fallback number saved');
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to save fallback number')),
  });

  return { create, update, activate, pause, remove, deleteNumber, updateFallback };
}
